import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import React from "react";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import { useSelector } from "react-redux";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import WifiIcon from "@mui/icons-material/Wifi";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import { PATH_APP } from "src/routes/paths";

/**
 * Diálogo de ayuda cuando detectamos que uno o más workers están offline.
 * Muestra pasos concretos ordenados por lo más probable primero: PC
 * apagado → worker no corriendo → internet caído → reinstalar.
 */
export default function WorkerOfflineHelpDialog({ open, onClose }) {
  const history = useHistory();
  const { workers } = useSelector((s) => s.workerStatus);
  const { isOnline: tpvOnline, avgLatencyMs } = useSelector((s) => s.networkStatus);

  const offline = workers?.filter((w) => !w.isOnline) || [];
  const workerNames = offline.map((w) => w.hostname || w.instanceId).join(", ");

  const goToInstall = () => {
    history.push(PATH_APP.tuCarta.printerInstall);
    onClose();
  };

  const goToStatus = () => {
    history.push(PATH_APP.tuCarta.printerSystemStatus);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <PowerSettingsNewIcon color="error" />
          <Typography variant="h6">Worker sin conexión</Typography>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Alert severity="error" sx={{ mb: 2 }}>
          <strong>Por qué importa:</strong> el worker es la app que envía las comandas a las
          impresoras. Sin él operativo, los tickets que el TPV genera no llegan a imprimirse —
          ni comandas de cocina ni tickets de caja.
        </Alert>

        {offline.length > 0 && (
          <Typography variant="body2" color="text.secondary" mb={2}>
            Worker(s) afectados: <strong>{workerNames}</strong>
          </Typography>
        )}

        <Typography variant="subtitle2" mb={1}>Revisa estas cosas en orden:</Typography>

        <List dense>
          <ListItem alignItems="flex-start">
            <ListItemIcon sx={{ minWidth: 36 }}><CheckCircleOutlineIcon fontSize="small" /></ListItemIcon>
            <ListItemText
              primary="1. ¿Está el PC del worker encendido?"
              secondary="El worker corre en el ordenador que tiene las impresoras conectadas. Si ese PC está apagado, reiniciando o hibernado, no hay worker."
            />
          </ListItem>

          <ListItem alignItems="flex-start">
            <ListItemIcon sx={{ minWidth: 36 }}><CheckCircleOutlineIcon fontSize="small" /></ListItemIcon>
            <ListItemText
              primary="2. ¿Ves el icono de TakeMyOrder en la bandeja del sistema?"
              secondary={
                <>
                  En Windows, abajo a la derecha junto al reloj. Si no está, abre el menú
                  Inicio → escribe <strong>TakeMyOrder</strong> → ejecuta "TakeMyOrder Worker".
                </>
              }
            />
          </ListItem>

          <ListItem alignItems="flex-start">
            <ListItemIcon sx={{ minWidth: 36 }}><RefreshIcon fontSize="small" /></ListItemIcon>
            <ListItemText
              primary="3. Reinicia la conexión del worker"
              secondary={
                <>
                  Click <strong>derecho</strong> sobre el icono TakeMyOrder en la bandeja →
                  "Reiniciar conexión". En 10-15 segundos debería volver a 🟢 online. Si
                  sigue offline, cierra el worker ("Salir") y vuélvelo a abrir.
                </>
              }
            />
          </ListItem>

          <ListItem alignItems="flex-start">
            <ListItemIcon sx={{ minWidth: 36 }}><WifiIcon fontSize="small" /></ListItemIcon>
            <ListItemText
              primary="4. Verifica la conexión a internet"
              secondary={
                <>
                  <Stack direction="row" spacing={1} alignItems="center" mt={0.5}>
                    <Typography variant="caption">Este TPV:</Typography>
                    {tpvOnline ? (
                      <Chip size="small" color="success" label={`Online · ${Math.round(avgLatencyMs ?? 0)}ms`} />
                    ) : (
                      <Chip size="small" color="error" label="Sin internet" />
                    )}
                  </Stack>
                  <Typography variant="caption" color="text.secondary" component="div" mt={0.5}>
                    Si este TPV está online pero el worker sigue offline, el problema está en
                    el PC del worker (su WiFi, su cable Ethernet o su conexión con el router).
                  </Typography>
                </>
              }
            />
          </ListItem>

          <ListItem alignItems="flex-start">
            <ListItemIcon sx={{ minWidth: 36 }}><DownloadIcon fontSize="small" /></ListItemIcon>
            <ListItemText
              primary="5. Último recurso: reinstalar el worker"
              secondary="Si tras los pasos anteriores sigue sin aparecer, descarga e instala el worker de nuevo desde la pestaña Instalar. La configuración se mantiene."
            />
          </ListItem>
        </List>

        <Divider sx={{ my: 2 }} />

        <Typography variant="caption" color="text.secondary">
          Si nada de esto funciona, contacta con soporte indicando el nombre del worker ({workerNames || "—"})
          y desde cuándo lleva offline.
        </Typography>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
        <Button variant="outlined" onClick={goToStatus}>Ver estado del worker</Button>
        <Button variant="contained" onClick={goToInstall} startIcon={<DownloadIcon />}>
          Ir a Instalar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
